import { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyDocumentKind, UploadPropertyFileRequest } from '@/shared/api';
import { AddMoreFiles, DropZone } from './AkteUploadAffordances';
import { FilesUploadRow } from './FilesUploadRow';
import {
  isFileRowValid,
  newFileRow,
  toUploadRequest,
  type FileUploadRow,
} from './filesUploadModel';
import { useMultiFileUpload } from './useMultiFileUpload';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File, metadata: UploadPropertyFileRequest) => Promise<void>;
  defaultKind: PropertyDocumentKind;
  /** Titel-Kontext, z.B. Kategorie-Name ("Abrechnungen") oder "Akte". */
  contextLabel: string;
  initialFiles?: File[];
  /** Ziel-Ordner (Custom-Ordner) — alle Zeilen landen dort statt im kanonischen Kind-Ordner. */
  targetFolder?: string;
}

const HEADERS = ['Datei', 'Kategorie', 'Datum', 'Betrag', 'Gegenpartei', ''];

export function UploadFilesModal({
  open,
  onOpenChange,
  onUpload,
  defaultKind,
  contextLabel,
  initialFiles,
  targetFolder,
}: Props) {
  const m = useMultiFileUpload<FileUploadRow, UploadPropertyFileRequest>({
    buildRow: (f) => newFileRow(f, defaultKind, 'EUR'),
    isValid: isFileRowValid,
    toMeta: (row) => ({ ...toUploadRequest(row), targetFolder: targetFolder ?? null }),
    upload: onUpload,
    invalidMessage: 'Bitte Beleg-Datum für alle datumpflichtigen Zeilen setzen.',
    noneMessage: 'Keine Datei hochgeladen — bitte Fehler prüfen',
    partialMessage: (s, t) => `${s} von ${t} hochgeladen — Rest siehe Fehlermeldungen`,
    successMessage: (n) => `${n} ${n === 1 ? 'Datei' : 'Dateien'} hochgeladen`,
    onAllDone: () => onOpenChange(false),
  });

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) {
      m.addFilesArray(initialFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  function handleClose(v: boolean) {
    if (!v && m.uploading) return;
    onOpenChange(v);
    if (!v) m.reset();
  }

  const title =
    m.rows.length === 0
      ? `Dateien hochladen — ${contextLabel}`
      : `${m.rows.length} ${m.rows.length === 1 ? 'Datei' : 'Dateien'} hochladen`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl" showClose={!m.uploading}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {m.rows.length === 0 ? (
            <DropZone
              onFiles={m.addFiles}
              hint="Mehrere Dateien auf einmal — pro Datei ein Beleg"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-ink-faint border-line/40 border-b text-left text-[10px] uppercase tracking-wider">
                  <tr>
                    {HEADERS.map((h, i) => (
                      <th
                        key={i}
                        className={i === HEADERS.length - 1 ? 'w-12 py-2' : 'py-2 pr-2 font-medium'}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {m.rows.map((row) => (
                    <FilesUploadRow
                      key={row.id}
                      row={row}
                      onChange={(patch) => m.patchRow(row.id, patch)}
                      onRemove={() => m.removeRow(row.id)}
                      disabled={m.uploading}
                    />
                  ))}
                </tbody>
              </table>
              <AddMoreFiles onFiles={m.addFiles} disabled={m.uploading} />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {m.rows.length > 0 && (
            <span className="text-ink-faint mr-auto text-xs tabular-nums">
              {m.rows.filter((r) => r.status === 'done').length}/{m.rows.length} hochgeladen
            </span>
          )}
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={m.uploading}>
            Abbrechen
          </Button>
          <Button variant="brand" disabled={!m.canSubmit} loading={m.uploading} onClick={m.submit}>
            {m.rows.length === 0
              ? 'Hochladen'
              : `${m.rows.length} ${m.rows.length === 1 ? 'Datei' : 'Dateien'} hochladen`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
